import { Link } from 'react-router-dom';
import { Breadcrumbs, organizationLd, webSiteLd, articleLd, faqLd, type FaqItem } from '../seo';
import type { ReactNode } from 'react';

const typography = '[&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-gray-900 [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:text-gray-700 [&_p]:leading-relaxed [&_p]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-3 [&_ul]:space-y-1 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-3 [&_ol]:space-y-1 [&_li]:leading-relaxed [&_a]:text-parksy-700 [&_a:hover]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-parksy-500 [&_blockquote]:bg-parksy-50 [&_blockquote]:px-4 [&_blockquote]:py-2 [&_blockquote]:my-4 [&_blockquote]:text-gray-700 [&_blockquote]:italic [&_table]:w-full [&_table]:text-sm [&_table]:border-collapse [&_table]:my-4 [&_th]:text-left [&_th]:px-3 [&_th]:py-2 [&_th]:border-b [&_th]:bg-gray-50 [&_th]:font-semibold [&_th]:text-gray-900 [&_td]:px-3 [&_td]:py-2 [&_td]:border-b [&_td]:text-gray-700';

export interface GuideFrontmatter {
  slug: string;
  title: string;
  description: string;
  ogImage: string;
  author?: string;
  publishedTime: string;
  modifiedTime?: string;
  readMinutes: number;
  category: 'cities' | 'ev' | 'two-wheeler' | 'airport' | 'parking-tips' | 'operator' | 'safety' | 'commute';
  related?: { to: string; label: string }[];
  faqs?: { question: string; answer: string }[];
}

export interface GuideProps extends GuideFrontmatter {
  children: ReactNode;
}

export default function GuideTemplate({
  slug,
  title,
  description,
  ogImage,
  author,
  publishedTime,
  modifiedTime,
  readMinutes,
  category,
  related = [],
  faqs = [],
  children,
}: GuideProps) {
  const canonical = `https://parksy.rent/guides/${slug}`;
  const modified = modifiedTime ?? publishedTime;

  const schemas = [
    organizationLd(),
    webSiteLd(),
    articleLd({
      headline: title,
      description,
      url: canonical,
      image: ogImage,
      datePublished: publishedTime,
      dateModified: modified,
      authorName: author ?? 'The Parksy team',
    }),
    ...(faqs.length > 0 ? [faqLd(faqs as FaqItem[])] : []),
  ];

  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'Home', to: '/' },
          { label: 'Guides', to: '/guides' },
          { label: title },
        ]}
        pageTitle={title}
        pageDescription={description}
        extraSchemas={schemas}
      />
      <article className="max-w-3xl mx-auto px-4 py-10">
        <header className="mb-8">
          <div className="text-xs uppercase tracking-wide text-parksy-700 font-semibold mb-2">
            {category.replace(/-/g, ' ')}
          </div>
          <h1 className="text-4xl font-bold text-gray-900 leading-tight mb-3">{title}</h1>
          <p className="text-lg text-gray-700 leading-relaxed">{description}</p>
          <div className="mt-4 flex items-center gap-3 text-sm text-gray-500">
            <span>By {author ?? 'The Parksy team'}</span>
            <span>·</span>
            <span>{new Date(publishedTime).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            <span>·</span>
            <span>{readMinutes} min read</span>
          </div>
        </header>

        <div className={`max-w-none ${typography}`}>
          {children}
        </div>

        {related.length > 0 && (
          <aside className="mt-12 border-t pt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Related guides</h2>
            <ul className="space-y-2 text-sm">
              {related.map((r) => (
                <li key={r.to}>
                  <Link to={r.to} className="text-parksy-700 hover:underline">
                    {r.label} →
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}

        <footer className="mt-12 border-t pt-6 text-xs text-gray-500">
          <p>
            Last updated {new Date(modified).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}.
            Data is crowdsourced and may contain outliers. Parksy filters reports and applies
            automatic anomaly detection.
          </p>
        </footer>
      </article>
    </>
  );
}
